/**
 * LiveScoresWidget — Live scores for any sport
 * Extracted from: fetchScores() lines 2501-2529, displayScores() lines 2651-2701
 * API: GET /scores/:sport (1-2 tokens)
 * CSS prefix: lsw-
 */

import { useState, useEffect, useCallback } from 'react'
import { fetchOddsSports, fetchOddsScores } from '../lib/api'
import { getSportIcon } from '../lib/oddsConstants'
import { Activity, RefreshCw, ChevronRight } from 'lucide-react'
import TeamLogo from './shared/TeamLogo'
import './LiveScoresWidget.css'

function LiveScoresWidget({ onQuotaUpdate }) {
  const [sports, setSports] = useState([])
  const [selectedSport, setSelectedSport] = useState('')
  const [daysFrom, setDaysFrom] = useState('')
  const [eventIds, setEventIds] = useState('')
  const [games, setGames] = useState([])
  const [loading, setLoading] = useState(false)
  const [sportsLoading, setSportsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [expanded, setExpanded] = useState(true)
  const [lastFetch, setLastFetch] = useState(null)

  useEffect(() => {
    async function loadSports() {
      setSportsLoading(true)
      const res = await fetchOddsSports()
      if (res.status === 'ok' && res.data) {
        setSports(res.data.filter(s => s.active && !s.has_outrights))
      }
      setSportsLoading(false)
    }
    loadSports()
  }, [])

  const fetchData = useCallback(async () => {
    if (!selectedSport) return
    setLoading(true)
    setError(null)

    const params = {}
    if (daysFrom) params.daysFrom = daysFrom
    if (eventIds.trim()) params.eventIds = eventIds.trim()

    const res = await fetchOddsScores(selectedSport, params)

    if (res.status === 'ok' && res.data) {
      setGames(res.data)
      setLastFetch(new Date())
      if (onQuotaUpdate && (res.remaining || res.used)) onQuotaUpdate(res.remaining, res.used)
    } else {
      setError(res.message || 'Failed to load scores')
      setGames([])
    }

    setLoading(false)
  }, [selectedSport, daysFrom, eventIds])

  const liveCount = games.filter(g => g.scores && !g.completed).length
  const finalCount = games.filter(g => g.completed).length
  const upcomingCount = games.filter(g => !g.scores && !g.completed).length

  function getGameStatus(game) {
    if (game.completed) return { label: 'Final', cls: 'final' }
    if (game.scores) return { label: 'Live', cls: 'live' }
    return { label: 'Upcoming', cls: 'upcoming' }
  }

  function getScore(game) {
    if (!game.scores) return '-'
    const away = game.scores.find(s => s.name === game.away_team)
    const home = game.scores.find(s => s.name === game.home_team)
    return `${away?.score ?? '0'} - ${home?.score ?? '0'}`
  }

  return (
    <div className="widget-card">
      <div
        className="widget-header lsw-header-clickable"
        onClick={() => setExpanded(e => !e)}
      >
        <Activity size={16} className="widget-icon" />
        <span className="widget-title">Live Scores</span>
        {games.length > 0 && (
          <span className="lsw-summary">
            {liveCount > 0 && <span className="lsw-live-badge">{liveCount} Live</span>}
            {finalCount > 0 && <span className="lsw-final-count">{finalCount} Final</span>}
            {upcomingCount > 0 && <span className="lsw-upcoming-count">{upcomingCount} Upcoming</span>}
          </span>
        )}
        <span className="status-badge warning" style={{ fontSize: 9 }}>1-2 TOKENS</span>
        <ChevronRight
          size={16}
          className={`lsw-chevron ${expanded ? 'rotated' : ''}`}
        />
      </div>

      {expanded && (
        <>
          {/* Controls */}
          <div className="lsw-controls">
            <select
              className="lsw-select"
              value={selectedSport}
              onChange={(e) => setSelectedSport(e.target.value)}
              disabled={sportsLoading}
            >
              <option value="">
                {sportsLoading ? 'Loading sports...' : 'Select a sport'}
              </option>
              {sports.map(s => (
                <option key={s.key} value={s.key}>
                  {getSportIcon(s.key)} {s.title}
                </option>
              ))}
            </select>

            <select
              className="lsw-select lsw-days"
              value={daysFrom}
              onChange={(e) => setDaysFrom(e.target.value)}
            >
              <option value="">Today only (1 token)</option>
              <option value="1">Last 1 day (2 tokens)</option>
              <option value="2">Last 2 days (2 tokens)</option>
              <option value="3">Last 3 days (2 tokens)</option>
            </select>

            <input
              type="text"
              className="lsw-input"
              placeholder="Event IDs (optional, comma-separated)"
              value={eventIds}
              onChange={(e) => setEventIds(e.target.value)}
            />

            <button
              className="lsw-fetch-btn"
              onClick={fetchData}
              disabled={!selectedSport || loading}
            >
              {loading ? <RefreshCw size={13} className="spinning" /> : <RefreshCw size={13} />}
              <span>Get Scores</span>
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="widget-body">
              <div className="widget-error">{error}</div>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="widget-body">
              <div className="widget-empty">Loading scores...</div>
            </div>
          )}

          {/* Scores table */}
          {!loading && games.length > 0 && (
            <div className="lsw-table-wrap">
              <table className="lsw-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Status</th>
                    <th>Away</th>
                    <th>Home</th>
                    <th>Score</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {games.map(game => {
                    const time = new Date(game.commence_time)
                    const timeStr = `${time.toLocaleDateString()} ${time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                    const status = getGameStatus(game)
                    const lastUpdate = game.last_update
                      ? new Date(game.last_update).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      : '-'

                    return (
                      <tr key={game.id} className={`lsw-row-${status.cls}`}>
                        <td className="lsw-time">{timeStr}</td>
                        <td>
                          <span className={`lsw-status lsw-status-${status.cls}`}>
                            {status.label}
                          </span>
                        </td>
                        <td className="lsw-team"><TeamLogo teamName={game.away_team} sportKey={selectedSport} />{game.away_team}</td>
                        <td className="lsw-team"><TeamLogo teamName={game.home_team} sportKey={selectedSport} />{game.home_team}</td>
                        <td className="lsw-score">{getScore(game)}</td>
                        <td className="lsw-updated">{lastUpdate}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Empty states */}
          {!loading && !error && games.length === 0 && selectedSport && lastFetch && (
            <div className="widget-body">
              <div className="widget-empty">No games found for this sport.</div>
            </div>
          )}

          {!loading && !selectedSport && (
            <div className="widget-body">
              <div className="widget-empty">Select a sport and click Get Scores to view live results.</div>
            </div>
          )}

          <div className="widget-footer">
            <span>{lastFetch ? `Updated ${lastFetch.toLocaleTimeString()}` : 'Not loaded'}</span>
            <span>{games.length > 0 && `${games.length} games`}</span>
          </div>
        </>
      )}
    </div>
  )
}

export default LiveScoresWidget
